import { _ as __rest, a as __awaiter } from './tslib.es6-DN65qNZS.js';
import React, { useState, useEffect, forwardRef, useRef } from 'react';
import { d as debounce } from './debounce-ClFpMKL7.js';
import classnames from 'classnames';
import { createPortal } from 'react-dom';
import { usePopper } from 'react-popper';
import { u as useOnKeyDown_2 } from './useOnKeyDown-t-UhpB2o.js';
import { u as useSafeLayoutEffect_1 } from './useSafeLayoutEffect-7WC_06jN.js';
import { u as useIsMounted_2 } from './useIsMounted-Bhu6Lxq6.js';
import { T as Text } from './Text-BCmEgJBp.js';
import { I as Icon } from './Icon-8KKXFsxm.js';
import { H as Heading } from './Heading-DX1jjqTV.js';
import { I as InputText } from './InputText-BcnP-GlH.js';

var styles = {"autocomplete":"v0Cys9vQ-NE-","options":"CX-N-e0e6og-","visible":"_4PlUaegVpkk-","option":"ucQtG0iwqbo-","active":"r8HS-ma79QE-","separator":"ydwLX44YFZc-","heading":"PvUgNoAUG7A-","icon":"DAOOFUZ1BRM-","text":"cjKJFup6hGY-","label":"hG5zNBuI2wA-","details":"_3CwjFCbhqi4-"};

var IndexChange;
(function (IndexChange) {
    IndexChange[IndexChange["Previous"] = -1] = "Previous";
    IndexChange[IndexChange["Next"] = 1] = "Next";
})(IndexChange || (IndexChange = {}));
// Adding useIsMounted is what tipped this to 13 statements.
// Any additions beyond useIsMounted should probably see this component refactored a bit
// eslint-disable-next-line max-statements
function Menu({ visible, options, selectedOption, onOptionSelect, attachTo, }) {
    const [highlightedIndex, setHighlightedIndex] = useState(0);
    const { menuRef, setMenuRef, styles: popperStyles, attributes, targetWidth, } = useRepositionMenu(attachTo, visible);
    const detectSeparatorCondition = (option) => option.description || option.details;
    const detectGroups = (option) => option.options;
    const addSeparators = options.some(detectSeparatorCondition);
    const initialHighlight = options.some(detectGroups) ? 1 : 0;
    setupKeyListeners();
    useEffect(() => setHighlightedIndex(initialHighlight), [options]);
    const mounted = useIsMounted_2();
    const menu = (React.createElement("div", Object.assign({ className: classnames(styles.options, { [styles.visible]: visible }), ref: setMenuRef, style: Object.assign(Object.assign({}, popperStyles.popper), { width: targetWidth }) }, attributes.popper), options.map((option, index) => {
        const optionClass = classnames(styles.option, {
            [styles.active]: index === highlightedIndex,
            [styles.separator]: addSeparators,
        });
        if (isGroup(option)) {
            return (React.createElement("div", { key: option.label, className: styles.heading },
                React.createElement(Heading, { level: 5 }, option.label)));
        }
        return (React.createElement("button", { className: optionClass, key: option.value, onMouseDown: onOptionSelect.bind(undefined, option) },
            React.createElement("div", { className: styles.icon }, isOptionSelected(selectedOption, option) && (React.createElement(Icon, { name: "checkmark", size: "small" }))),
            React.createElement("div", { className: styles.text },
                React.createElement("div", { className: styles.label },
                    React.createElement(Text, null, option.label),
                    option.description !== undefined && (React.createElement(Text, { variation: "subdued" }, option.description))),
                option.details !== undefined && (React.createElement("div", { className: styles.details },
                    React.createElement(Text, null, option.details))))));
    })));
    return mounted.current ? createPortal(menu, document.body) : menu;
    function setupKeyListeners() {
        useEffect(() => {
            var _a, _b;
            (_b = (_a = menuRef === null || menuRef === void 0 ? void 0 : menuRef.children[highlightedIndex]) === null || _a === void 0 ? void 0 : _a.scrollIntoView) === null || _b === void 0 ? void 0 : _b.call(_a, {
                behavior: "smooth",
                block: "nearest",
                inline: "start",
            });
        }, [highlightedIndex]);
        useOnKeyDown_2((event) => {
            const indexChange = arrowKeyPress(event, IndexChange.Next);
            if (indexChange) {
                setHighlightedIndex(Math.min(options.length - 1, highlightedIndex + indexChange));
            }
        }, "ArrowDown");
        useOnKeyDown_2((event) => {
            const indexChange = arrowKeyPress(event, IndexChange.Previous);
            if (indexChange) {
                setHighlightedIndex(Math.max(0, highlightedIndex + indexChange));
            }
        }, "ArrowUp");
        useOnKeyDown_2((event) => {
            if (!visible)
                return;
            if (isGroup(options[highlightedIndex]))
                return;
            event.preventDefault();
            onOptionSelect(options[highlightedIndex]);
        }, "Enter");
    }
    function arrowKeyPress(event, direction) {
        if (!visible)
            return;
        event.preventDefault();
        const requestedIndex = options[highlightedIndex + direction];
        return requestedIndex && isGroup(requestedIndex)
            ? direction + direction
            : direction;
    }
}
function isOptionSelected(selectedOption, option) {
    return selectedOption && selectedOption.value === option.value;
}
function isGroup(option) {
    if (option.options)
        return true;
    return false;
}
function useRepositionMenu(attachTo, visible = false) {
    var _a;
    const [menuRef, setMenuRef] = useState();
    const popper = usePopper(attachTo.current, menuRef, {
        modifiers: [
            { name: "offset", options: { offset: [0, 8] } },
            { name: "flip", options: { fallbackPlacements: ["top"] } },
        ],
    });
    useSafeLayoutEffect_1(() => {
        var _a;
        (_a = popper === null || popper === void 0 ? void 0 : popper.update) === null || _a === void 0 ? void 0 : _a.call(popper);
    }, [visible]);
    const targetWidth = (_a = attachTo.current) === null || _a === void 0 ? void 0 : _a.clientWidth;
    return Object.assign(Object.assign({}, popper), { menuRef, setMenuRef, targetWidth });
}

// Max statements increased to make room for the debounce functions
/* eslint max-statements: ["error", 14] */
function AutocompleteInternal(_a, ref) {
    var _b;
    var { initialOptions = [], value, allowFreeForm = true, size = undefined, debounce: debounceRate = 300, onChange, getOptions, placeholder, onBlur, onFocus, validations } = _a, inputProps = __rest(_a, ["initialOptions", "value", "allowFreeForm", "size", "debounce", "onChange", "getOptions", "placeholder", "onBlur", "onFocus", "validations"]);
    const [options, setOptions] = useState(initialOptions);
    const [menuVisible, setMenuVisible] = useState(false);
    const [inputText, setInputText] = useState((_b = value === null || value === void 0 ? void 0 : value.label) !== null && _b !== void 0 ? _b : "");
    const autocompleteRef = useRef(null);
    const delayedSearch = debounce(updateSearch, debounceRate);
    useEffect(() => {
        delayedSearch();
        return delayedSearch.cancel;
    }, [inputText]);
    useEffect(() => {
        var _a;
        updateInput((_a = value === null || value === void 0 ? void 0 : value.label) !== null && _a !== void 0 ? _a : "");
    }, [value]);
    return (React.createElement("div", { className: styles.autocomplete, ref: autocompleteRef },
        React.createElement(InputText, Object.assign({ ref: ref, autocomplete: false, size: size, value: inputText, onChange: handleInputChange, placeholder: placeholder, onFocus: handleInputFocus, onBlur: handleInputBlur, validations: validations }, inputProps)),
        menuVisible && (React.createElement(Menu, { attachTo: autocompleteRef, visible: menuVisible && options.length > 0, options: options, selectedOption: value, onOptionSelect: handleMenuChange }))));
    function updateInput(newText) {
        setInputText(newText);
        if (newText === "") {
            setOptions(mapToOptions(initialOptions));
        }
    }
    function updateSearch() {
        return __awaiter(this, void 0, void 0, function* () {
            const updatedOptions = yield getOptions(inputText);
            const filteredOptions = updatedOptions.filter((option) => "options" in option && option.options ? option.options.length > 0 : true);
            setOptions(mapToOptions(filteredOptions));
        });
    }
    function handleMenuChange(chosenOption) {
        onChange(chosenOption);
        updateInput(chosenOption.label);
        setMenuVisible(false);
    }
    function handleInputChange(newText) {
        updateInput(newText);
        if (allowFreeForm) {
            onChange({ label: newText });
        }
    }
    function handleInputBlur() {
        setMenuVisible(false);
        if (value == undefined || value.label !== inputText) {
            setInputText("");
            onChange(undefined);
        }
        onBlur && onBlur();
    }
    function handleInputFocus() {
        setMenuVisible(true);
        if (onFocus) {
            onFocus();
        }
    }
}
function mapToOptions(items) {
    return items.reduce(function (result, item) {
        result = result.concat([item]);
        if (item.options) {
            result = result.concat(item.options);
        }
        return result;
    }, []);
}
const Autocomplete = forwardRef(AutocompleteInternal);

export { Autocomplete as A };
